import { Box, Grid, makeStyles } from '@material-ui/core'
import { differenceBy } from 'lodash'
import { useEffect, useLayoutEffect, useState } from 'react'
import {
  ArrayField,
  Datagrid,
  Loading,
  TextField,
  useNotify,
  useRefresh,
  useShowController,
} from 'react-admin'
import { useHistory } from 'react-router-dom'
import { Divider } from 'shared/components/Base/Base'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { Button } from 'shared/components/Button/Button'
import { SimpleDialog } from 'shared/components/Dialog/SimpleDialog'
import { CheckboxDropdown } from 'shared/components/Dropdown/CheckboxDropdown'
import { Text } from 'shared/components/Text/Text'
import { TextInput } from 'shared/components/TextInput/TextInput'
import { Handler } from 'shared/helpers/typeHelper'
import { darkGray, errorD1, lightGray1, primaryBlack, successD1 } from 'shared/theme/colors'
import { hexWithOpacity } from 'shared/theme/utils'

import { getAllPermissions, updatePermissionGroup } from '../../api/permissions'
import { Permission, PermissionGroup } from '../../models/permission'

export const useStyles = makeStyles(() => ({
  gridRow: {
    display: 'grid',
    gap: '20px',
    gridTemplateColumns: '170px 424px',
    alignItems: 'center',
  },
}))

const CustomUserShow = (props: any) => {
  const { loaded, record, basePath } = useShowController<PermissionGroup>(props)
  const classes = useStyles()
  const [isLoading, setIsLoading] = useState(false)
  const [confirm, setConfirm] = useState(false)
  const [name, setName] = useState('')
  const [nameError, setNameError] = useState('')
  const [allPermissions, setAllPermissions] = useState<Permission[] | null>(null)
  const [selectedPermissions, setSelectedPermissions] = useState<Permission[]>([])
  const notify = useNotify()
  const refresh = useRefresh()
  const history = useHistory()

  useLayoutEffect(() => {
    if (record?.name) {
      setName(record.name)
    }
    if (record?.permissions) {
      setSelectedPermissions(record?.permissions)
    }
  }, [record?.name, record?.permissions])

  useEffect(() => {
    getAllPermissions().then(setAllPermissions)
  }, [])

  if (!record || !allPermissions || isLoading || !basePath) {
    return <Loading />
  }

  function getDropdownOptions() {
    if (!allPermissions) return []

    return allPermissions.map((permission) => ({
      key: permission.name,
      label: `${permission.content_type.app_label} | ${permission.content_type.name} | ${permission.name}`,
      value: selectedPermissions.some((p) => p.id === permission.id),
      onChange: (selected: boolean) => {
        setSelectedPermissions(
          selected
            ? [...selectedPermissions.filter((p) => p.id !== permission.id), permission]
            : selectedPermissions.filter((p) => p.id !== permission.id)
        )
      },
    }))
  }

  async function handleSave() {
    if (!name || !record?.id) return
    setConfirm(true)
  }

  async function handleConfirm() {
    if (!name || !record?.id) return
    setIsLoading(true)
    await updatePermissionGroup(String(record.id), {
      name,
      permissions: selectedPermissions.map((p) => p.id),
    })
    setConfirm(false)
    setIsLoading(false)
    notify('Success!', { type: 'success' })
    refresh()
  }

  function handleNameChange(e: React.ChangeEvent<HTMLInputElement>) {
    setName(e.target.value)
    if (!e.target.value) {
      setNameError('Name is required')
    } else {
      setNameError('')
    }
  }

  const addedPermissions = differenceBy(selectedPermissions, record.permissions, 'id')
  const removedPermissions = differenceBy(record.permissions, selectedPermissions, 'id')

  return loaded ? (
    <Grid container direction="column" spacing={3} alignItems="baseline" justifyContent="flex-start">
      <Grid item style={{ width: '100%' }}>
        <FlexBox gap="20px" justifyContent="space-between" alignItems="center">
          <Text variant="header2" mt={2} fontColor={primaryBlack}>
            Edit group
          </Text>

          <FlexBox gap="20px">
            <Text variant="header3" fontColor={darkGray} align="left">
              {addedPermissions.length
                ? `${addedPermissions.length} permission${addedPermissions.length > 1 ? 's' : ''} added`
                : null}
              {removedPermissions.length
                ? `${addedPermissions.length ? ',' : ''} ${removedPermissions.length} permission${
                    removedPermissions.length > 1 ? 's' : ''
                  } removed`
                : null}
            </Text>
            <Button variant="secondary" size="medium" onClick={() => history.push(basePath)}>
              Cancel
            </Button>
            <Button variant="primary" size="medium" onClick={handleSave}>
              Save
            </Button>
          </FlexBox>
        </FlexBox>

        <Box mt={4} />
        <div className={classes.gridRow}>
          <div>
            <Text variant="paragraph1" fontColor={lightGray1}>
              Name:
            </Text>
          </div>
          <div>
            <TextInput
              type="text"
              value={name}
              onChange={handleNameChange}
              error={nameError ? 'This field is required' : undefined}
            />
          </div>
        </div>

        <Box mt={6} />
        <div className={classes.gridRow}>
          <div>
            <Text variant="paragraph1" fontColor={lightGray1}>
              Permissions:
            </Text>
          </div>
          <div>
            <Text variant="header3" fontColor={darkGray}>
              {selectedPermissions.length} / {allPermissions.length} Selected
            </Text>
            <Box mt={1} />
            <CheckboxDropdown
              placeholder="Type to filter"
              options={getDropdownOptions()}
              align="left"
              fontSize="14px"
              maxHeight={500}
              width={600}
            />
          </div>
        </div>

        <Divider mt={5} mb={5} />

        {confirm && (
          <ConfirmModal
            addedPermissions={addedPermissions}
            removedPermissions={removedPermissions}
            permissionGroup={record}
            handleConfirm={handleConfirm}
            handleCancel={() => setConfirm(false)}
          />
        )}
      </Grid>

      <Grid item style={{ width: '100%' }}>
        <ArrayField
          source="permissions"
          record={{
            ...record,
            permissions: [...record.permissions, ...addedPermissions],
          }}
          resource="permissions"
        >
          <Datagrid
            rowStyle={(record) =>
              addedPermissions.some((p) => p.id === record.id)
                ? { backgroundColor: hexWithOpacity(successD1, 0.2) }
                : removedPermissions.some((p) => p.id === record.id)
                ? { backgroundColor: hexWithOpacity(errorD1, 0.2) }
                : {}
            }
          >
            <TextField source="content_type.app_label" label="App label" />
            <TextField source="content_type.name" label="Content type" />
            <TextField source="name" label="Permission" />
            <TextField source="codename" label="Code Name" />
          </Datagrid>
        </ArrayField>
      </Grid>
    </Grid>
  ) : null
}

interface ConfirmModalProps {
  addedPermissions: Permission[]
  removedPermissions: Permission[]
  permissionGroup: PermissionGroup
  handleCancel: Handler
  handleConfirm: Handler
}

function ConfirmModal({
  addedPermissions,
  removedPermissions,
  permissionGroup,
  handleCancel,
  handleConfirm,
}: ConfirmModalProps) {
  const hasChanges = addedPermissions.length || removedPermissions.length

  function renderPermission(permission: Permission, prefix: string) {
    return (
      <Text variant="paragraph2" fontColor={prefix === '+' ? successD1 : errorD1} align="left">
        <span style={{ width: '10px', display: 'inline-block' }}>{prefix}</span>{' '}
        {permission.content_type.app_label} | {permission.content_type.name} | {permission.name}
      </Text>
    )
  }

  return (
    <SimpleDialog onClose={handleCancel} isOpen>
      <Text variant="header2" align="left">
        Are you sure you want to change {permissionGroup.name} permissions?
      </Text>
      <Box mt={3} />
      {hasChanges ? (
        <>
          {removedPermissions.map((p) => renderPermission(p, '-'))}
          {addedPermissions.map((p) => renderPermission(p, '+'))}
        </>
      ) : null}
      <Box m={5} />
      <Button onClick={handleConfirm} size="large" margin="0 auto">
        Confirm
      </Button>
      <Box m={3} />
      <Button margin="0 auto" variant="text" onClick={handleCancel}>
        Cancel
      </Button>
    </SimpleDialog>
  )
}

export default CustomUserShow
